import { ImportService } from '@amzn/awscat-portfolio-assessment-service-client';
import { ADSCredentials } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Alert, Container, SpaceBetween } from '@amzn/awsui-components-react';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { match, useHistory, useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { HelpPanelTopicOptions } from '../../common/Constants';
import { constants } from '../../common/Localization';
import { ADSRegions, regions } from '../../common/regions';
import { MatchParam } from '../../common/types';
import { HelpPanelContext } from '../../contexts/HelpPanelContext';
import AWSUIFormBuilder, { MetadataItem } from '../common/AWSUIFormBuilder';

type Props = FlashContextInterface;
const importService = new ImportService();
const initialAwsCredentials = {
    accessKeyId: '',
    secretAccessKey: '',
    region: ADSRegions[0],
};
const ImportFromADS: FunctionComponent<Props> = ({flash}): JSX.Element => {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });
    const portfolioId = match?.params?.portfolioId;

    const isMounted = useIsMounted();
    const [ loading, setLoading ] = useState(false);
    const { panelHandler } = useContext(HelpPanelContext);
    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.IMPORT_FROM_ADS);
    }, [ panelHandler ]);

    const history = useHistory();
    const formMetadata: MetadataItem<ADSCredentials>[] = [
        {
            identifier: 'accessKeyId',
            type: 'string',
            label: constants.ACCESS_KEY_ID,
            placeholder: constants.ACCESS_KEY_ID_PLACEHOLDER,
            validation: {
                minLen: 16,
                maxLen: 128,
            },
            required: true,
        },
        {
            identifier: 'secretAccessKey',
            type: 'password',
            label: constants.SECRET_ACCESS_KEY,
            placeholder: constants.SECRET_ACCESS_KEY_PLACEHOLDER,
            validation: {
                minLen: 1,
                maxLen: 500,
            },
            required: true,
        },
        {
            identifier: 'region',
            type: 'enum',
            label: constants.REGION_LABEL,
            placeholder: constants.REGION_PLACEHOLDER,
            enum: ADSRegions.map(region => ({value: region, text: regions[region].label})),
        }
    ];
    async function onFormSubmit(item: ADSCredentials) {
        if (!portfolioId) {
            return;
        }
        if (isMounted()){
            setLoading(true);
        }
        try {
            await importService.importADS(portfolioId, item);
            history.push(`/portfolio/${portfolioId}/management/assets/servers`);
            flash.alert(FlashType.success, constants.SUCCESSFULLY_IMPORT_ADS, '');
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.FAILED_TO_IMPORT_ADS);
        } finally {
            if (isMounted()){ //TODO: Remove isMounted() dependency by properly aborting requests
                setLoading(false); 
            }
        }
    }
    return (
        <SpaceBetween size='l' data-testid='ImportFromADS'>
            <Alert header={constants.PLEASE_NOTE}>
                <ul>
                    <li>{constants.ADS_NOTE_1}</li>
                    <li>{constants.ADS_NOTE_2}</li>
                    <li>{constants.ADS_NOTE_3}</li>
                    <li>{constants.ADS_NOTE_4}</li>
                    <li>{constants.ADS_NOTE_5}</li>
                    <li>{constants.ADS_NOTE_6}</li>
                    <li>{constants.ADS_NOTE_7}</li>
                </ul>
            </Alert>
            <Container header={<h2>{constants.ADS_CREDENTIALS_HEADER}</h2>}>
                <AWSUIFormBuilder
                    item={initialAwsCredentials}
                    metadata={formMetadata}
                    onSubmit={onFormSubmit}
                    primaryAction={constants.IMPORT_FROM_ADS_BUTTON}
                    loading={loading}
                ></AWSUIFormBuilder>
            </Container>
        </SpaceBetween>
    );
};

export default withFlashContext(ImportFromADS);
